import { Button } from "@/shared/components/form/Button";
import { InputSelect } from "@/shared/components/form/InputSelect";
import type { QuoterTranslations } from "@/shared/lang/interfaces/quoter";
import { QUOTER_HISTORY_CURRENCY_OPTIONS } from "../constants/quoter-history.constants";
import type { QuoterHistoryFiltersState } from "../types/quoter-history.types";

type SelectOption = { label: string; value: string };

interface QuoterHistoryFiltersProps {
  filters: QuoterHistoryFiltersState;
  setFilters: React.Dispatch<
    React.SetStateAction<QuoterHistoryFiltersState>
  >;
  onApply: () => void;
  onReset: () => void;
  isLoading?: boolean;
  t: (k: keyof QuoterTranslations, opts?: Record<string, unknown>) => string;
}

export const QuoterHistoryFilters = ({
  filters,
  setFilters,
  onApply,
  onReset,
  isLoading = false,
  t,
}: QuoterHistoryFiltersProps) => {
  const currencyOptions: SelectOption[] = [
    { value: "", label: t("history_filters_all") },
    ...QUOTER_HISTORY_CURRENCY_OPTIONS,
  ];

  return (
    <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
        {t("history_filters_title")}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("history_filters_origin")}
          </label>
          <InputSelect<SelectOption>
            options={currencyOptions}
            value={
              filters.origin_currency
                ? currencyOptions.find(
                    (o) => o.value === filters.origin_currency
                  ) ?? null
                : currencyOptions[0] ?? null
            }
            onChange={(selected) => {
              const v = Array.isArray(selected)
                ? selected[0]?.value
                : selected?.value;
              setFilters((prev) => ({
                ...prev,
                origin_currency: v ?? "",
              }));
            }}
            placeholder={t("history_filters_all")}
            maxWidth="w-full"
            controlClassName="!h-11"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("history_filters_destination")}
          </label>
          <InputSelect<SelectOption>
            options={currencyOptions}
            value={
              filters.destination_currency
                ? currencyOptions.find(
                    (o) => o.value === filters.destination_currency
                  ) ?? null
                : currencyOptions[0] ?? null
            }
            onChange={(selected) => {
              const v = Array.isArray(selected)
                ? selected[0]?.value
                : selected?.value;
              setFilters((prev) => ({
                ...prev,
                destination_currency: v ?? "",
              }));
            }}
            placeholder={t("history_filters_all")}
            maxWidth="w-full"
            controlClassName="!h-11"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("history_filters_start_date")}
          </label>
          <input
            type="date"
            value={filters.start_date}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, start_date: e.target.value }))
            }
            className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            {t("history_filters_end_date")}
          </label>
          <input
            type="date"
            value={filters.end_date}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, end_date: e.target.value }))
            }
            className="w-full px-3 py-3 border border-gray-300 dark:border-zinc-700 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>
      <div className="flex gap-3 mt-4">
        <Button
          type="button"
          variant="primary"
          onClick={onApply}
          disabled={isLoading}
        >
          {t("history_filters_apply")}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onReset}
          disabled={isLoading}
        >
          {t("history_filters_clear")}
        </Button>
      </div>
    </div>
  );
};
